"use client";

import { useState, useCallback, useEffect } from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useTurnstile } from "../../hooks/use-turnstile";

// Import step components
import { BasicInformationStep } from "./components/BasicInformationStep";
import { RaceDetailsStep } from "./components/RaceDetailsStep";
import { PreviewStep } from "./components/PreviewStep";

// Import custom hooks
import { useCustomizationState } from "./hooks/useCustomizationState";

// Side Panel Component
function SidePanel({
  step,
  size,
  onPrev,
  onNext,
  isSubmitting,
  pendingCheckout,
  turnstileLoading,
  turnstileError,
  onCheckout,
  canContinue,
}: {
  step: number;
  size: string;
  onPrev: () => void;
  onNext: () => void;
  isSubmitting: boolean;
  pendingCheckout: boolean;
  turnstileLoading: boolean;
  turnstileError: string | null;
  onCheckout: () => void;
  canContinue: boolean;
}) {
  return (
    <div className="sticky top-6 space-y-6">
      {step >= 3 && (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <h3 className="text-xl font-bold font-spartan">Order Summary</h3>
              <div className="flex justify-between">
                <span>Premium Quarter Zip</span>
                <span>$250.00</span>
              </div>
              <div className="flex justify-between">
                <span>Size</span>
                <span>{size?.toUpperCase()}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {(step === 1 || step === 2 || step === 3) && (
        <div className="flex gap-2 justify-end mt-6">
          {step > 1 && (
            <Button variant="outline" onClick={onPrev} disabled={isSubmitting}>
              Back
            </Button>
          )}
          {step < 3 && (
            <Button onClick={onNext} disabled={!canContinue || isSubmitting}>
              Continue
            </Button>
          )}
          {step === 3 && (
            <Button
              onClick={onCheckout}
              disabled={
                !canContinue ||
                isSubmitting ||
                pendingCheckout ||
                turnstileLoading
              }
            >
              {pendingCheckout || turnstileLoading || isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="loader spinner-border spinner-border-sm" />{" "}
                  Processing...
                </span>
              ) : (
                "Proceed to payment"
              )}
            </Button>
          )}
        </div>
      )}
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <h3 className="text-xl font-bold font-spartan">Need Help?</h3>
            <p className="text-sm text-gray-500">
              If you have any questions about the customization process or need
              assistance, our team is here to help.
            </p>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-5 w-5"
              >
                <rect width="20" height="16" x="2" y="4" rx="2" />
                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
              </svg>
              <span className="text-sm">team@tritanclub.com</span>
            </div>
          </div>
        </CardContent>
      </Card>
      {turnstileError && (
        <div className="text-sm text-red-500 mt-2">{turnstileError}</div>
      )}
    </div>
  );
}

// Progress Indicator Component
function ProgressIndicator({ step }: { step: number }) {
  return (
    <div className="mb-8">
      <Progress value={step * 25} className="h-2" />
      <div className="mt-2 flex justify-between text-sm">
        <span className={step >= 1 ? "font-medium" : "text-gray-500"}>
          Basic Information
        </span>
        <span className={step >= 2 ? "font-medium" : "text-gray-500"}>
          Race Details
        </span>
        <span className={step >= 3 ? "font-medium" : "text-gray-500"}>
          Preview
        </span>
        <span className={step >= 4 ? "font-medium" : "text-gray-500"}>
          Checkout
        </span>
      </div>
    </div>
  );
}

// Page Layout Component
function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col">
      <header className="border-b">
        <div className="container flex h-16 items-center justify-center px-4 md:px-6">
          <div className="flex items-center justify-center">
            <Link to="/">
              <img
                src="/icons/logo-with-text-black.png"
                alt="Triathlon Achievements Logo"
                width={120}
                height={40}
                className="h-10 w-auto"
                style={{ maxWidth: "100%", objectFit: "contain" }}
              />
            </Link>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="container px-4 py-6 md:px-6 md:py-12">
          <div className="mb-8">
            <h1 className="text-2xl font-bold tracking-tighter font-spartan">
              Customize Your Quarter Zip
            </h1>
            <p className="text-gray-500"></p>
          </div>
          {children}
        </div>
      </main>
      <footer className="border-t">
        <div className="container py-6 md:py-8">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <p className="text-center text-sm text-gray-500 md:text-left">
              {new Date().getFullYear()} Tritan. All rights reserved.
            </p>
            <div className="flex gap-4">
              <Link
                to="https://instagram.com/tritanclub/"
                className="text-gray-500 hover:text-gray-900"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="h-5 w-5"
                >
                  <rect width="20" height="20" x="2" y="2" rx="5" ry="5" />
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                  <line x1="17.5" x2="17.51" y1="6.5" y2="6.5" />
                </svg>
                <span className="sr-only">Instagram</span>
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Main Component
export default function CustomizePage() {
  const {
    form,
    setForm,
    isLoaded,
    validateForm,
    updateRaces,
    updateEmail,
    updateSize,
  } = useCustomizationState();

  useEffect(() => {
    window.fbq("track", "InitiateCheckout");
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const {
    userId,
    token,
    registerUser,
    loading: turnstileLoading,
    error: turnstileError,
  } = useTurnstile("0x4AAAAAABXtfHka-jb2cr9X", "customize");
  const [pendingCheckout, setPendingCheckout] = useState(false);

  // Convert races to counts for components that still use the old format
  const raceCounts = {
    long: form.races.filter((r) => r.distance === "long").length,
    middle: form.races.filter((r) => r.distance === "middle").length,
    olympic: form.races.filter((r) => r.distance === "olympic").length,
    sprint: form.races.filter((r) => r.distance === "sprint").length,
    other: form.races.filter((r) => r.distance === "other").length,
  };

  // Handle registration of user with email
  const handleRegistration = useCallback(
    async (email: string, size: string) => {
      try {
        const registeredUserId = await registerUser(email, size);
        if (registeredUserId) {
          // Registration successful
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error during registration:", error);
        return false;
      }
    },
    [registerUser]
  );

  // Handle checkout process
  const handleCheckout = useCallback(async () => {
    if (!form.size) {
      alert("Please select a size for your quarter zip");
      return;
    }
    if (!form.email) {
      alert("Please enter your email address");
      return;
    }
    if (!userId) {
      // Try to register the user first
      const registered = await handleRegistration(form.email, form.size);
      if (!registered) {
        alert("Registration failed. Please try again.");
        return;
      }
    }

    setIsSubmitting(true);
    const formData = new FormData();

    // We now use the userId for authentication instead of turnstile token
    formData.append("userId", userId!);
    formData.append("size", form.size);

    const racesData = form.races.map((race) => ({
      id: race.id,
      name: race.name,
      distance: race.distance,
    }));
    formData.append("races", JSON.stringify(racesData));

    try {
      const response = await fetch("/api/checkout", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(
          `Checkout failed: ${response.status} ${response.statusText}`
        );
      }

      const data = (await response.json()) as {
        success: boolean;
        redirectUrl?: string;
        error?: string;
      };

      if (data.success) {
        if (!data.redirectUrl) {
          throw new Error("No redirect URL provided");
        }
        window.location.href = data.redirectUrl;
      } else {
        setIsSubmitting(false);
        alert(`Checkout failed: ${data.error || "Unknown error"}`);
      }
    } catch (error) {
      setIsSubmitting(false);
      alert(
        `Error during checkout: ${
          error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }, [form, userId, handleRegistration]);

  const initiateCheckout = async () => {
    setSubmitAttempted(true);
    const error = validateForm();
    if (error) {
      return;
    }
    setPendingCheckout(true);
    try {
      await handleCheckout();
    } catch (err) {
      console.error("Checkout error:", err);
    } finally {
      setPendingCheckout(false);
    }
  };

  const handleNextStep = async () => {
    if (form.step === 1) {
      setSubmitAttempted(true);
      const error = validateForm();
      if (error) {
        return;
      }

      // Register the user if we have email and size
      if (form.email && form.size && !userId) {
        try {
          setIsSubmitting(true);
          const success = await handleRegistration(form.email, form.size);
          if (!success) {
            alert("Failed to register. Please try again.");
            setIsSubmitting(false);
            return;
          }
        } catch (err) {
          console.error("Registration error:", err);
          setIsSubmitting(false);
          return;
        } finally {
          setIsSubmitting(false);
        }
      }

      setForm((prevForm) => ({ ...prevForm, step: 2 }));
    } else if (form.step === 2) {
      if (form.races.length === 0) return;
      setForm((prevForm) => ({ ...prevForm, step: 3 }));
    }
  };

  const handlePrevStep = () => {
    if (form.step > 1) {
      setForm((prevForm) => ({ ...prevForm, step: prevForm.step - 1 }));
    }
  };

  // Determine if the "Continue" button should be enabled
  const canContinue = (() => {
    if (form.step === 1) {
      return (
        !!form.email &&
        /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email) &&
        !!form.size
      );
    }
    if (form.step === 2) {
      return form.races.length > 0;
    }
    return !validateForm(); // for step 3
  })();

  // Don't render until we've attempted to load from localStorage
  if (!isLoaded && typeof window !== "undefined") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-2xl font-bold font-spartan">
            Loading your data...
          </div>
          <Progress value={50} className="h-2 w-60" />
        </div>
      </div>
    );
  }

  return (
    <Layout>
      <ProgressIndicator step={form.step} />
      <div className="grid gap-8 lg:grid-cols-3">
        <div className="lg:col-span-2">
          {form.step === 1 && (
            <BasicInformationStep
              email={form.email}
              size={form.size}
              onEmailChange={updateEmail}
              onSizeChange={updateSize}
              submitAttempted={submitAttempted}
            />
          )}
          {form.step === 2 && (
            <RaceDetailsStep races={form.races} onUpdateRaces={updateRaces} />
          )}
          {form.step === 3 && <PreviewStep raceCounts={raceCounts} />}
        </div>
        <SidePanel
          step={form.step}
          size={form.size}
          onPrev={handlePrevStep}
          onNext={handleNextStep}
          isSubmitting={isSubmitting}
          pendingCheckout={pendingCheckout}
          turnstileLoading={turnstileLoading}
          turnstileError={turnstileError}
          onCheckout={initiateCheckout}
          canContinue={canContinue}
        />
      </div>
    </Layout>
  );
}
